import { useCallback } from 'react'
import type { FC, KeyboardEvent, VFC } from 'react'
import { Asset } from '@opallabs/cassini'
import { useBackboneUserRole, UserRoleSystemNames } from '@opallabs/hooks'
import { extractInitials } from '@opallabs/lambda'
import type { WithTheme } from '@opallabs/lapidary'
import { Icon, Tooltip } from '@opallabs/lapidary'
import { kuiperClassNames } from '../utils/kuiperClassNames'
import * as styles from './styles'
import type { AvatarContainerProps, AvatarProps } from './types'

export type { AvatarProps }

const handleKeyPress = (e: KeyboardEvent, onClick: () => void): void => {
  if (['Enter', ' '].includes(e.key)) onClick()
}

type RolePopoverContentProps = Readonly<
  WithTheme & {
    role?: UserRoleSystemNames
  }
>
export const rolePopoverContent = ({
  role,
  theme = 'light',
}: RolePopoverContentProps): JSX.Element => (
  <div css={styles.rolePopover({ theme })}>
    <div>
      {role === 'view_only' && 'Viewer Role'}
      {role === 'limited' && 'Limited Role'}
    </div>
    <div>
      This user role has limited access to many capabilities throughout Opal.
      For more information, please contact your administrator.
    </div>
  </div>
)

const AvatarContainer: FC<React.PropsWithChildren<AvatarContainerProps>> = ({
  ariaHidden = false,
  children,
  displayRole = false,
  onClick,
  size,
  styleOverrides,
  theme = 'light',
  unread = false,
  userRole,
}) => {
  const { userRole: role = userRole } = useBackboneUserRole()

  const shouldDisplayRole =
    displayRole && role !== undefined && ['limited', 'view_only'].includes(role)
  const clickable = typeof onClick === 'function'

  const clickHandler = useCallback(() => {
    if (typeof onClick === 'function') onClick()
  }, [onClick])

  return (
    /* eslint-disable jsx-a11y/no-static-element-interactions */
    <div
      aria-hidden={ariaHidden}
      className={kuiperClassNames('avatar-outer-wrapper')}
      css={styles.container({
        shouldDisplayRole,
        size,
        theme,
      })}
    >
      {shouldDisplayRole && (
        <Tooltip
          content={rolePopoverContent({
            role,
            theme,
          })}
          side="bottom"
          align="end"
          sideOffset={10}
          zIndex={999}
          legacyTheme="dark"
          showArrow={true}
        >
          <span css={styles.roleLabel({ size })}>
            {role === 'view_only' && 'Viewer'}
            {role === 'limited' && 'Limited'}
          </span>
        </Tooltip>
      )}
      <div
        css={[
          styles.avatarContainer({
            clickable,
            shouldDisplayRole,
            size,
            theme,
          }),
          styleOverrides,
        ]}
        className={kuiperClassNames('avatar')}
        onClick={clickHandler}
        onKeyDown={(e): void => handleKeyPress(e, clickHandler)}
        tabIndex={typeof onClick === 'function' ? 0 : undefined}
      >
        {children}
      </div>
      {unread && (
        <div
          css={styles.badge({
            shouldDisplayRole,
            size,
            theme,
          })}
        />
      )}
    </div>
  )
}
AvatarContainer.displayName = 'Kuiper: Avatar ← AvatarContainer'

/**
 * Display an avatar and, optionally, role for a user. The avatar will be
 * either an asset, the user's initials, or a generic icon (in order of
 * preference).
 */
export const Avatar: VFC<AvatarProps> = ({
  ariaHidden = false,
  avatarUuid,
  displayRole = false,
  iconGlyph = 'person',
  name,
  onClick,
  size,
  styleOverrides,
  theme = 'light',
  unread = false,
  userRole,
  withPopover = false,
}) => {
  const containerProps: AvatarContainerProps = {
    ariaHidden,
    displayRole,
    onClick,
    size,
    styleOverrides,
    theme,
    unread,
    userRole,
  }

  const getAvatarInternals = () => {
    // Display User's profile picture
    if (typeof avatarUuid === 'string') {
      return (
        <Asset
          ariaLabel={name ? `${name} Profile Picture` : 'User Profile Picture'}
          ariaMode={ariaHidden ? 'hidden' : 'visible'}
          assetReference={{ uuid: avatarUuid, asset_type: 'image' }}
          transforms={{ height: size * 2, width: size * 2 }}
        />
      )
    }

    // Display User's Initials
    if (typeof name === 'string' && name.length > 0) {
      const initials = extractInitials(name, { maximumInitials: 2 })
      if (!ariaHidden) return initials
      return <span aria-hidden>{initials}</span>
    }

    // Fall back to icon
    return (
      <Icon
        ariaHidden={ariaHidden}
        glyph={iconGlyph}
        useHypernova={true}
        height={size}
        width={size}
      />
    )
  }

  const avatar = (
    <AvatarContainer {...containerProps}>
      {getAvatarInternals()}
    </AvatarContainer>
  )

  return withPopover ? (
    <Tooltip legacyTheme="dark" content={name ?? ''} side="bottom" zIndex={999}>
      {avatar}
    </Tooltip>
  ) : (
    avatar
  )
}
Avatar.displayName = 'Kuiper: Avatar'
